import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, ChevronLeft, ChevronRight, ClipboardCheck, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const KG_LBS = [{ v: 'kg', l: 'kg' }, { v: 'lbs', l: 'lbs' }];
const CM_FT = [{ v: 'cm', l: 'cm' }, { v: 'ft', l: 'feet' }];

/** V73 — the mother fills her diet assessment inside the chat.
 *  Paginated by section, autosaved after every answer, and she must re-check everything
 *  before it turns into the document her dietician works from. */
export const DietAssessmentForm = ({ consultId, open, onClose, onSubmitted }) => {
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [missing, setMissing] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/medical/consult/${consultId}/form`);
      setForm(res.data.form);
      setAnswers(res.data.form?.answers || {});
    } catch (e) {
      toast.error('Could not open the form');
    } finally { setLoading(false); }
  }, [consultId]);

  useEffect(() => { if (open) { setStep(0); setConfirming(false); load(); } }, [open, load]);

  const sections = form?.sections || [];
  const section = sections[step];
  const allQuestions = useMemo(() => sections.flatMap(s => s.questions), [sections]);
  const answeredCount = allQuestions.filter(q => {
    const v = answers[q.key]?.value;
    return v !== undefined && v !== null && String(v).trim() !== '';
  }).length;

  const setAnswer = (q, value, extra = {}) => {
    setAnswers(prev => ({ ...prev, [q.key]: { ...(prev[q.key] || {}), value, prefilled: false, ...extra } }));
    setMissing(m => m.filter(k => k !== q.key));
  };

  const saveDraft = async (next) => {
    setSaving(true);
    try {
      await api.post(`/api/medical/consult/${consultId}/form/draft`, { answers });
    } catch (e) { /* draft saves are best-effort; her typing is never blocked */ }
    finally { setSaving(false); if (typeof next === 'number') setStep(next); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/api/medical/consult/${consultId}/form/submit`, { answers, confirmed: true });
      toast.success('Sent to your nutritionist');
      setConfirming(false);
      onSubmitted?.(res.data);
      onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === 'ANSWERS_MISSING') {
        setMissing(d.missing || []);
        setConfirming(false);
        const firstIdx = sections.findIndex(s => s.questions.some(q => (d.missing || []).includes(q.key)));
        if (firstIdx >= 0) setStep(firstIdx);
        toast.error(d.message || 'Some answers are missing');
      } else {
        toast.error((typeof d === 'object' ? d?.message : d) || 'Could not submit the form');
      }
    } finally { setSubmitting(false); }
  };

  const renderQuestion = (q) => {
    const a = answers[q.key] || {};
    const isMissing = missing.includes(q.key);
    const unitOptions = q.unit === 'kg_or_lbs' ? KG_LBS : q.unit === 'cm_or_ft' ? CM_FT : null;
    return (
      <div key={q.key} className="space-y-1.5" data-testid={`diet-form-q-${q.key}`}>
        <Label className={`text-sm font-medium ${isMissing ? 'text-destructive' : ''}`}>
          {q.label}{q.required ? '' : <span className="text-muted-foreground font-normal"> (optional)</span>}
        </Label>
        {q.help ? <p className="text-[11px] text-muted-foreground">{q.help}</p> : null}
        {q.type === 'choice' ? (
          <div className="flex flex-wrap gap-1.5">
            {(q.options || []).map(opt => (
              <button key={opt} type="button" onClick={() => setAnswer(q, opt)}
                data-testid={`diet-form-opt-${q.key}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${a.value === opt
                  ? 'border-emerald-600 bg-emerald-600 text-white'
                  : 'border-border bg-background hover:bg-muted'}`}>
                {opt}
              </button>
            ))}
            {String(a.value || '').startsWith('Any other') && (
              <Input className="mt-1" placeholder="Please mention" value={a.other_text || ''}
                onChange={e => setAnswer(q, a.value, { other_text: e.target.value })}
                data-testid={`diet-form-other-${q.key}`} />
            )}
          </div>
        ) : q.type === 'number' ? (
          <div className="flex gap-2">
            <Input type="number" inputMode="decimal" value={a.value ?? ''} className={isMissing ? 'border-destructive' : ''}
              onChange={e => setAnswer(q, e.target.value)} data-testid={`diet-form-input-${q.key}`} />
            {unitOptions && (
              <div className="flex rounded-md border overflow-hidden shrink-0">
                {unitOptions.map(u => (
                  <button key={u.v} type="button" onClick={() => setAnswer(q, a.value, { unit: u.v })}
                    className={`px-2.5 text-xs ${(a.unit || unitOptions[0].v) === u.v ? 'bg-emerald-600 text-white' : 'bg-background'}`}
                    data-testid={`diet-form-unit-${q.key}-${u.v}`}>{u.l}</button>
                ))}
              </div>
            )}
            {q.unit && !unitOptions ? <span className="self-center text-xs text-muted-foreground">{q.unit}</span> : null}
          </div>
        ) : (
          <Textarea rows={q.key === 'present_diet_pattern' ? 5 : 2} value={a.value ?? ''}
            className={isMissing ? 'border-destructive' : ''}
            onChange={e => setAnswer(q, e.target.value)} data-testid={`diet-form-input-${q.key}`} />
        )}
        {a.prefilled ? (
          <p className="text-[11px] text-emerald-700 flex items-center gap-1">
            <Sparkles className="h-3 w-3" />We filled this in from your profile — please correct it if it's changed
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { saveDraft(); onClose?.(); } }}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="diet-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-emerald-600" />{form?.title || 'Diet assessment'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : !form ? (
          <p className="text-sm text-muted-foreground py-6">This form is no longer open.</p>
        ) : confirming ? (
          <div className="space-y-3" data-testid="diet-form-confirm">
            <p className="text-sm">
              Please take one last look. Once you confirm, your answers go to your nutritionist as a
              document and she'll start building your chart.
            </p>
            <div className="rounded-xl border divide-y max-h-[46vh] overflow-y-auto">
              {allQuestions.map(q => (
                <div key={q.key} className="px-3 py-2">
                  <p className="text-[11px] text-muted-foreground">{q.label}</p>
                  <p className="text-sm">
                    {String(answers[q.key]?.value ?? '—')}{answers[q.key]?.unit ? ` ${answers[q.key].unit}` : ''}
                    {answers[q.key]?.other_text ? ` — ${answers[q.key].other_text}` : ''}
                  </p>
                </div>
              ))}
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => setConfirming(false)} data-testid="diet-form-back-to-edit">
                Go back and edit
              </Button>
              <Button onClick={submit} disabled={submitting} data-testid="diet-form-confirm-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Yes, send it
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{section?.title} · step {step + 1} of {sections.length}</span>
                <span data-testid="diet-form-progress">{answeredCount}/{allQuestions.length} answered{saving ? ' · saving…' : ''}</span>
              </div>
              <Progress value={(answeredCount / Math.max(1, allQuestions.length)) * 100} className="h-1.5" />
            </div>

            <div className="space-y-4">{(section?.questions || []).map(renderQuestion)}</div>

            <DialogFooter className="gap-2 sm:gap-2">
              {step > 0 && (
                <Button variant="outline" onClick={() => saveDraft(step - 1)} data-testid="diet-form-prev">
                  <ChevronLeft className="h-4 w-4 mr-1" />Back
                </Button>
              )}
              {step < sections.length - 1 ? (
                <Button onClick={() => saveDraft(step + 1)} data-testid="diet-form-next">
                  Next<ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={async () => { await saveDraft(); setConfirming(true); }} data-testid="diet-form-review">
                  Review my answers
                </Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
